import React, { useEffect, useRef } from 'react'
import MuiModal from '@mui/material/Modal'
import type { CSSObject } from '@emotion/react'

import Box, { type BoxProps } from '../box'
import { CloseIcon } from '../icons'
import Pressable, { type PressableProps } from '../pressable'
import {
  KitElement,
  VIEW_BASE,
  distribute,
  flexContainerStyles,
  paddingStyles,
  toDOMStyle
} from '../_support'

/* -----------------------------------------------------------------------------
 * Modal
 *
 * The @walrus/common Modal over MUI's Modal. Like modal-react-native-web, the
 * content stays mounted while closed (hidden with `display: none`), sits in a
 * fixed layer at z-index 9999, and draws its own half-transparent overlay that
 * closes the modal on click. MUI adds Escape to close and moves focus into the
 * modal while it is open, then back to where it was.
 *
 * Padding and flex container props style the content, everything else the
 * white container around it.
 * -------------------------------------------------------------------------- */

// the full-screen layer centering the container; MUI moves focus to it
const layerCss = toDOMStyle({
  ...VIEW_BASE,
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
  outline: 'none',
  width: '100%'
}) as CSSObject

// modal-react-native-web's fixed layer, a View
const rootStyle = {
  alignItems: 'stretch',
  border: '0 solid black',
  boxSizing: 'border-box',
  flexDirection: 'column',
  flexShrink: 0,
  zIndex: 9999
} as const

const contentKeys = new Set([
  ...Object.keys(paddingStyles),
  ...Object.keys(flexContainerStyles)
])

const medium: BoxProps = {
  borderRadius: 'xlarge',
  marginY: '24',
  marginX: '12',
  paddingX: '24',
  paddingY: '24'
}

const CloseButton = (props: PressableProps) => (
  <Pressable position='absolute' top={8} right={8} p='12' {...props}>
    <CloseIcon width={20} height={20} fill='violet' />
  </Pressable>
)

export type ModalProps = BoxProps & {
  children?: React.ReactNode
  // on the close button, a click on the overlay, Escape, and whenever `open` turns false
  onRequestClose?: () => void
  open: boolean
  showCloseButton?: boolean
}

const Modal = ({
  children,
  onRequestClose,
  open,
  showCloseButton = true,
  ...rest
}: ModalProps) => {
  const [contentProps, containerProps] = distribute(
    { ...medium, ...rest },
    { using: contentKeys }
  )

  // modal-react-native-web called onRequestClose (as onDismiss) on every close,
  // including the ones that did not start from the modal
  const wasOpen = useRef(open)
  useEffect(() => {
    if (wasOpen.current && !open) onRequestClose?.()
    wasOpen.current = open
  }, [open])

  return (
    <MuiModal
      open={open}
      onClose={() => onRequestClose?.()}
      keepMounted
      hideBackdrop
      disableScrollLock
      // pickers and menus inside a modal render in the app's portal host
      disableEnforceFocus
      aria-modal='true'
      style={{ ...rootStyle, display: open ? 'flex' : 'none' }}
    >
      <KitElement kitCss={layerCss}>
        <Pressable
          onPress={onRequestClose}
          position='absolute'
          left={0}
          top={0}
          width='100%'
          height='100%'
        >
          <Box width='100%' height='100%'>
            <Box bg='shade09' opacity={0.5} width='100%' height='100%' />
          </Box>
        </Pressable>
        <Box
          flex='0 1 auto'
          flexDirection='row'
          bg='white'
          {...containerProps}
          // the slide animation's resting transform
          style={{ transform: [{ translateY: 0 }] }}
        >
          <Box flex='1 1 auto'>
            <Box flex='1 1 auto'>
              <Box zIndex={0} flex='1 1 auto' {...contentProps}>
                {children}
              </Box>
              {showCloseButton && <CloseButton onPress={onRequestClose} />}
            </Box>
          </Box>
        </Box>
      </KitElement>
    </MuiModal>
  )
}

export default Modal
